import { ifr } from '@/common/messaging/ifr';
import { amd } from '@/common/utilities/amd';
import { eventEmitterMixin } from '@/common/messaging/event-emitter-mixin';
import { Iab2 } from '@/providers/iab2';
import { GoogleAcm } from '@/providers/google-acm';
import { Tc } from '@/providers/tc';
import { providerCommon } from '@/providers/provider-common';
import { eventBus } from 'shared/event-bus';
import { alphabetical } from '@/common/utilities/sort';
import fetch from 'unfetch';

export const privacyCenterMixin = {
  mixins: [eventEmitterMixin],
  data() {
    return {
      /** Trust Providers */
      iab: {},
      acm: {},
      tc: {
        categoryList: [],
      },

      /** Internal */
      debugMode: false,
      initialized: false,
      activeView: 'main',
      error: {
        neutral: '',
      },
      iabSortedVendors: [],
      vendorFilter: null,
      display: {
        purposes: [],
        features: [],
        specialFeatures: [],
        specialPurposes: [],
      },
      lastButtonToShowVendors: null,

      /** From postMessage */
      customCss: '',
      settings: {
        id: null,
        version: '',
        isPreview: false,
        reactived: '', //unused
        headerContent: 'Privacy Center',
        headerBgColor: '#283645',
        headerColor: '#fff',
        bodyContent: '<p>Top text</p>',
        bodyBgColor: '#CBCBCB',
        bodyColor: '#333333',
        btContent: 'Save',
        btTitle: 'Save your consent',
        btType: 'button',
        btBgColor: '#EB0149',
        btColor: '#fff',
        actionType: 'optout',
        vendorDefaultOpt: 'optout',
        ntrlErrorMsg: 'You must give your consent or refuse cookies for each category before saving your preferences.',
        categories: [],
        categoriesEnabled: [''],
        sizes: {
          // unused
          width: null,
          height: null,
        },
        labelBtOn: 'Oui',
        labelBtOff: 'Non',
        labelYesForAll: 'Accept All',
        labelNoForAll: 'Refuse all',
        blockedOnStyle: 0, // For switch when locked to on, 1 = Check, 0 = text
        defaultConfig: false, //unused
        vendorlistUrl: '', //From PostMessage for iab
        vendorlistBaseUrl: '', //From PostMessage for iab
        vendorsIntroduction: null,
        showPurposesText: null,
        showVendorsText: null,
        showRelatedVendorsText: null,
        acceptAllText: null,
        acceptAllTextColor: '#fff',
        acceptAllBgColor: '#EB0149',
        refuseAllText: null,
        refuseAllTextColor: '#fff',
        refuseAllBgColor: '#EB0149',
        cardHeadingBgColor: '#333333',
        cardHeadingFontColor: '#333333',
        cardBodyBgColor: '#333333',
        cardBodyFontColor: '#333333',
        btFocusColor: '#ffffff',
        switchFocusColor: '#363636',
        pcSwitchLabel: null,
        iabItemsToDisplay: {
          purposes: [],
          features: [],
          specialFeatures: [],
          specialPurposes: [],
        },
        includedVendorIds: [],
        tcIncludedVendors: {},
        iabVendorDefaultOpt: 'optout',
        iabCategoriesDefaultOpt: 'optout',
        iabLegitimateInterestToggle: false,
        tcCategoriesSectionTop: false,
        locale: null,
        reverseAllConsentButtonsOrder: 0,
        allConsentButtonsPosition: 'header',
        hideAcceptAll: false,
        hideRefuseAll: false,
        saveOnAllConsentButton: 1,
        allConsentPerView: 0,
        vendorSyncMode: 'onSave', //onSave or realTime
        consentButtonStyle: '', // default will be switch
      },
    };
  },
  computed: {
    closeButtonColor() {
      if (this.settings.bodyBgColor !== this.settings.btColor) {
        return this.settings.btColor;
      } else {
        return this.settings.btBgColor;
      }
    },

    isIabEnabled() {
      return this.settings.vendorlistUrl != null && this.settings.vendorlistUrl !== '';
    },
  },
  async mounted() {
    const searchParams = new URLSearchParams(document.location.search);
    this.debugMode = searchParams.get('debug') != null;
    if (this.debugMode) {
      // eslint-disable-next-line no-console
      console.info('Debug enabled. Informations available at "window.pcDebug"');
      window.pcDebug = {
        pc: this,
        init: null,
        reInit: [],
        consentsSent: [],
        getConfig() {
          return JSON.stringify({
            init: window.pcDebug.init,
            reInit: window.pcDebug.reInit[window.pcDebug.reInit.length - 1],
          });
        },
      };
    }
    const configUrl = searchParams.get('config');
    if (configUrl) {
      const response = await fetch(configUrl);
      const { init, reInit } = await response.json();
      await this.initListener(init);
      await this.reInitListener(reInit);
    } else {
      ifr.add(['init'], this.initListener);
      ifr.add(['reInit'], this.reInitListener);
      ifr.sendReady();
    }
  },

  methods: {
    async initListener(message) {
      if (this.debugMode) {
        window.pcDebug.init = JSON.parse(JSON.stringify(message));
      }
      Object.assign(this.settings, message.data);
      this.customCss = message.customCss;
      this.addStyle(this.customCss);
      await this.bootload({ jsHooksUrl: message.jsHooksUrl, hasBeforeHook: message.hasBeforeHook, eventBus });
    },
    async reInitListener(message) {
      if (this.debugMode) {
        window.pcDebug.reInit.push(JSON.parse(JSON.stringify(message)));
      }
      if (this.isIabEnabled) {
        this.iab.reInitConsents(message.data);
        if (this.settings.useIabGoogleAcm) {
          this.acm.reInitConsents(message.data);
        }
      }
      this.tc.reInitConsents(message.data);
      if (this.isIabEnabled || this.tc.hasVendors) {
        //Modern and legacy message support
        message.data.showPurposes || message.showPurposes ? this.showMainView() : this.showVendorsView();
      } else {
        this.showMainView();
      }
      await this.emitPrivacyCenterReInit();
    },
    async bootload({ jsHooksUrl, hasBeforeHook }) {
      if (!jsHooksUrl) {
        await this.init();
        ifr.send('loadedIframe', '');
        this.emitPrivacyCenterBootloadedEnded();
      } else {
        const hooksPromise = amd.hooks(jsHooksUrl, {
          setAllConsents: this.setAllConsents,
          addTranslation: this.$addTranslation,
          setLocale: this.$setLocale,
          iab: {
            useStackIds: (...stackIds) => this.iab.useStackIds(...stackIds),
            useAutoStack: () => this.iab.useAutoStack(),
          },
          tc: {
            categoriesSectionTop: value => (this.settings.tcCategoriesSectionTop = value),
          },
        });
        if (hasBeforeHook) {
          const hooks = await hooksPromise;
          await this.init();
          // Many implementations are too reliant of existing DOM
          // Moving before hook to after init with delay to let Vue render
          setTimeout(() => {
            if (hooks.before) {
              hooks.before();
            }
            if (hooks.after) {
              hooks.after();
            }
            ifr.send('loadedIframe', '');
            this.emitPrivacyCenterBootloadedEnded();
          }, 0);
        } else {
          await this.init();
          const hooks = await hooksPromise;
          setTimeout(() => {
            if (hooks.after) {
              hooks.after();
            }
            ifr.send('loadedIframe', '');
            this.emitPrivacyCenterBootloadedEnded();
          }, 0);
        }
      }
    },
    async init() {
      if (this.settings.locale) {
        this.$setLocale(this.settings.locale);
      }
      window.document.title = this.settings.headerContent;

      // IAB + Google ACM
      if (this.isIabEnabled) {
        this.iab = new Iab2({
          baseUrl: this.settings.vendorlistBaseUrl,
          url: this.settings.vendorlistUrl,
        });
        const iabPromise = this.iab.initVendorList(this.$getLocale(), this.settings.iabItemsToDisplay.vendors, {
          displayLegInt: this.settings.iabLegitimateInterestToggle,
          vendorDefaultOpts: this.settings.iabVendorDefaultOpt,
        });

        let acmPromise = null;
        if (this.settings.useIabGoogleAcm) {
          this.acm = new GoogleAcm({
            baseUrl: this.settings.vendorlistBaseUrl,
            categories: this.settings.categories,
            includedVendors: this.settings.acmIncludedVendors,
          });
          acmPromise = this.acm.initConsents(
            providerCommon.getConsentReducerFromSettings({ actionType: this.settings.iabVendorDefaultOpt })
          );
        }
        await Promise.all([iabPromise, acmPromise]);
        this.display = this.iab.computeDisplay(this.settings.iabItemsToDisplay.categories);
        this.iabSortedVendors = Object.values(this.display.vendors).sort(alphabetical(v => v.name, true));
        this.iab.setAllConsents(
          this.settings.iabCategoriesDefaultOpt === 'neutral' ? null : false,
          ['purposes', 'specialFeatures', 'stacks'],
          this.settings.iabLegitimateInterestToggle
        );
      }

      //TC
      this.tc = new Tc(this.settings.categories, this.settings.tcIncludedVendors);
      this.tc.initPurposeConsents(providerCommon.getConsentReducerFromSettings(this.settings));
      this.tc.initVendorConsents(
        providerCommon.getConsentReducerFromSettings({ actionType: this.settings.vendorDefaultOpt })
      );
      if (this.settings.vendorSyncMode === 'realTime') {
        this.tc.enableRealTimeCategoryVendorSync();
      }
      this.initialized = true;
    },
    showMainView() {
      this.activeView = 'main';
      this.emitPrivacyCenterViewChanged('main');
      const purposeSection = document.getElementById('purpose-section');
      if (purposeSection) {
        this.$nextTick(() => {
          purposeSection.focus();
        });
      }
    },
    showVendorsView({ category, provider, consentMode, $event } = {}) {
      if (category && provider) {
        this.vendorFilter = {
          category: category?.category, //Only for IAB
          id: category.id,
          provider,
        };
        if (consentMode) {
          this.vendorFilter.consentMode = consentMode;
        }
      } else {
        this.vendorFilter = null;
      }
      this.activeView = 'vendors';
      if ($event) {
        this.lastButtonToShowVendors = $event.target;
      }
      this.emitPrivacyCenterViewChanged('vendors');
      ifr.send('hitCounter', JSON.stringify([this.settings.id, 'pc_vendors']));

      const vendorSection = document.getElementById('vendor-section');
      if (vendorSection) {
        this.$nextTick(() => {
          vendorSection.focus();
        });
      }
    },

    isIabVendorDisplayed(vendor) {
      if (this.vendorFilter) {
        if (this.vendorFilter.provider !== 'iab') {
          return false;
        }
        if (this.vendorFilter.category === 'purposes') {
          if (this.vendorFilter.consentMode && vendor[this.vendorFilter.consentMode]) {
            return vendor[this.vendorFilter.consentMode]?.includes(this.vendorFilter.id);
          }
          return (
            vendor.purposes?.includes(this.vendorFilter.id) || vendor.legIntPurposes?.includes(this.vendorFilter.id)
          );
        } else {
          return vendor[this.vendorFilter.category]?.includes(this.vendorFilter.id);
        }
      }
      return true;
    },
    isAcmVendorDisplayed(vendor) {
      if (this.vendorFilter) {
        if (this.vendorFilter.provider !== 'tc') {
          return false;
        }
        return vendor.categories.includes(this.vendorFilter.id);
      }
      return true;
    },
    isTcVendorDisplayed(vendor) {
      if (this.vendorFilter) {
        if (this.vendorFilter.provider !== 'tc') {
          return false;
        }
        return vendor.categories.includes(this.vendorFilter.id);
      }
      return true;
    },
    setAllCategoryConsents(value) {
      if (this.isIabEnabled) {
        this.iab.setAllConsents(value, ['purposes', 'specialFeatures', 'stacks']);
      }
      this.tc.setAllConsents(value, ['purposes']);
    },
    setAllVendorConsents(value) {
      if (this.isIabEnabled) {
        this.iab.setAllConsents(value, ['vendors']);
      }
      this.tc.setAllConsents(value, ['vendors']);
      if (this.settings.useIabGoogleAcm) {
        this.acm.setAllConsents(value, ['vendors']);
      }
    },

    setAllConsents(value) {
      this.setAllCategoryConsents(value);
      this.setAllVendorConsents(value);
    },

    /**
     *
     * @param {boolean} value
     * @deprecated Replaced with allConsentHandler
     */
    saveAllWithConsent(value) {
      this.setAllConsents(value);
      this.save();
    },

    allConsentHandler(value) {
      if (this.settings.allConsentPerView == 1) {
        if (this.activeView === 'main') {
          this.setAllCategoryConsents(value);
        }
        if (this.activeView === 'vendors') {
          this.setAllVendorConsents(value);
        }
      } else {
        this.setAllConsents(value);
      }
      if (this.settings.saveOnAllConsentButton == 1) {
        this.save();
      }
    },

    closeButton() {
      ifr.send('close', '"cross"');
    },
    addStyle(cssContent) {
      const styleAnchor = document.createElement('style');
      styleAnchor.type = 'text/css';
      styleAnchor.innerHTML = cssContent;
      document.head.appendChild(styleAnchor);
    },
    save() {
      if (this.initialized) {
        this.tc.processNeutralVendors();
        if (!this.tc.hasExpressedConsents()) {
          this.error.neutral = this.settings.ntrlErrorMsg;
          return;
        }
      } else {
        this.error.neutral = '';
      }
      let consents;
      if (this.isIabEnabled) {
        this.iab.processNeutralVendors(this.iabLegitimateInterestToggle);
        consents = this.iab.getConsents();
        const tcConsents = this.tc.getConsents();
        consents.categories = tcConsents.categories.concat(consents.categories);
        consents.optinVendors = tcConsents.optinVendors.concat(consents.optinVendors);
        if (this.settings.useIabGoogleAcm) {
          const acmConsents = this.acm.getConsents();
          consents.optinVendors = acmConsents.optinVendors.concat(consents.optinVendors);
        }
      } else {
        consents = this.tc.getConsents();
      }
      // hotfix: retrocompat before Google ACM development
      if (consents.optinVendors) {
        const optinVendors = consents.optinVendors.map(Number).filter(v => {
          return Number.isNaN(v) === false;
        });

        // only numeric ids, no acm vendors for example
        if (optinVendors.length === consents.optinVendors.length) {
          consents.optinVendors = optinVendors;
        }
      }
      if (this.debugMode) {
        window.pcDebug.consentsSent.push(consents);
      }
      ifr.sendSetCat(this.settings, consents);
    },
  },
};
